import { useEffect, useState } from "react";
import { LocaleCode } from "next.i18n";
import { useRouter } from "next/router";
import { useRunOnVisibleOnce } from "src/hooks/useIntersectionObserver";
import usePriceFormatter from "src/lib/hooks/usePriceFormatter";
import { AddToCartPayload } from "src/lib/queries/data/cartData";
import { CatalogProduct, CatalogVariant } from "src/lib/services/catalog/types";
import { MergedOption } from "src/lib/services/util/optionRendering";
import makeProductUrl from "src/lib/util/makeProductUrl";
import { toKeyValue } from "src/lib/util/toKeyValue";

import { LineItem } from "../Cart/components/LineItem";
import { formatOptionValues } from "../util";

import selectors from "./selectors";

export type AddOnProps = {
  item: {
    title: string;
    id: string;
    slug: string;
    options: MergedOption[];
    quantity: number;
  };
  variant: CatalogVariant;
  product: CatalogProduct;
  onAdd: (payload: AddToCartPayload) => void;
  onViewed: () => void;
  onClicked: () => void;
};
export function AddOn({
  item,
  variant,
  product,
  onAdd,
  onViewed,
  onClicked,
}: AddOnProps) {
  const formatPrice = usePriceFormatter();
  const router = useRouter();
  const divRef = useRunOnVisibleOnce(onViewed, { threshold: 0.8 }, 500);

  const [quantity, setQuantity] = useState(item.quantity);
  // Keep in sync with item.quantity
  useEffect(() => {
    setQuantity(item.quantity);
  }, [item.quantity]);

  const image = {
    src: variant.cardImage?.url || "",
    width: variant.cardImage?.width,
    height: variant.cardImage?.height,
    alt: variant.cardImage?.description || variant.cardImage?.title,
  };

  const { primaryOptionValue, optionValues } = formatOptionValues(
    item.options,
    variant.selectedOptions
  );

  const handleAddToCart = () => {
    onAdd({
      location: selectors.addOnLocation,
      locale: router.locale as LocaleCode,
      lineItems: [
        {
          product: { ...product, title: item.title },
          quantity,
          variant,
          customAttributes: toKeyValue({ _list: selectors.addOnLocation }),
        },
      ],
    });
  };

  return (
    <LineItem
      ref={divRef}
      title={item.title}
      href={makeProductUrl(item.slug, variant.id)}
      image={image}
      primaryOptionValue={primaryOptionValue}
      price={formatPrice(variant.price)}
      optionValues={optionValues}
      onAdd={handleAddToCart}
      quantity={quantity}
      onQuantityChange={setQuantity}
      minQuantity={1}
      onAnalyticsClick={onClicked}
    />
  );
}
